import { m } from "#/paraglide/messages";
import type { ReactNode } from "react";
import type { AdminStatHistorySoort } from "#/modules/admin.statistics/api.statistics-admin.server";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@beweegbaas/ui";
import { createFileRoute } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import {
  getAdminDashboardData,
  getAdminStatHistory,
} from "#/modules/admin.statistics/dashboard.functions";
import { MonthlyBarChart } from "#/modules/util.charts/monthly-bar-chart";
import { WeeklyTrendChart } from "#/modules/util.charts/weekly-trend-chart";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/admin/dashboard/")({
  loader: () => getAdminDashboardData(),
  component: AdminDashboard,
  errorComponent: DashboardError,
});

function DashboardError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

const RESULTATEN_SERIES = [
  { label: m["admin.dashboard.door_leerling"](), color: "var(--chart-1)" },
  { label: m["admin.dashboard.door_docent"](), color: "var(--chart-2)" },
  { label: "Sociaal-emotioneel", color: "var(--chart-3)" },
];

const HISTORY_WEEKS = 26;

interface StatHistory {
  stat: AdminStatHistorySoort;
  /** Chart line label; falls back to the tile title. */
  label?: string;
  /** Shown under the dialog title, e.g. to flag an approximated series. */
  description?: string;
}

interface HistoryState {
  status: "loading" | "loaded" | "error";
  rows: { weekStart: Date; serie: string; count: number }[];
}

function StatTile({
  title,
  value,
  subtitle,
  icon,
  history,
}: {
  title: string;
  value: ReactNode;
  subtitle?: ReactNode;
  icon: ReactNode;
  history?: StatHistory;
}) {
  const [open, setOpen] = useState(false);
  const [historyState, setHistoryState] = useState<HistoryState | null>(null);

  const card = (
    <Card
      size="sm"
      className={`h-full ${history ? "hover:bg-muted/50 cursor-pointer transition-colors" : ""}`}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <CardTitle>{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent className="mt-auto space-y-1">
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        {subtitle && <p className="text-muted-foreground text-sm text-balance">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  if (!history) return card;

  const serie = history.label ?? title;

  const onOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen || historyState?.status === "loaded") return;

    setHistoryState({ status: "loading", rows: [] });
    getAdminStatHistory({ data: { stat: history.stat, weeks: HISTORY_WEEKS } })
      .then((response) => {
        setHistoryState({
          status: "loaded",
          rows: response.points.map((point) => ({
            weekStart: new Date(point.weekStart),
            serie,
            count: point.count,
          })),
        });
      })
      .catch(() => setHistoryState({ status: "error", rows: [] }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <button
        type="button"
        className="h-full rounded-xl text-left"
        onClick={() => onOpenChange(true)}
        aria-label={`${title} — toon verloop`}
      >
        {card}
      </button>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {history.description && <DialogDescription>{history.description}</DialogDescription>}
        </DialogHeader>
        {historyState?.status === "loaded" && (
          <WeeklyTrendChart
            as="div"
            rows={historyState.rows}
            series={[{ label: serie, color: "var(--chart-1)" }]}
            ariaLabel={`${title} per week`}
          />
        )}
        {historyState?.status === "loading" && (
          <div className="text-muted-foreground flex h-70 items-center justify-center text-sm">
            Verloop laden…
          </div>
        )}
        {historyState?.status === "error" && (
          <div className="text-muted-foreground flex h-70 items-center justify-center text-sm">
            {m["common.something_went_wrong"]()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const iconClassName = "text-muted-foreground size-6 shrink-0";

function AdminDashboard() {
  const { overview, trends } = Route.useLoaderData();

  const actieveScholenPct =
    overview.actieveScholen > 0
      ? Math.round((overview.actieveScholenMetRecenteLes / overview.actieveScholen) * 100)
      : 0;

  const lessenRows = trends.lessenAfgerondPerWeek.map((week) => ({
    weekStart: new Date(week.weekStart),
    serie: m["admin.dashboard.lessen_afgerond"](),
    count: week.count,
  }));

  const resultatenRows = trends.resultatenPerWeek.flatMap((week) => [
    {
      weekStart: new Date(week.weekStart),
      serie: m["admin.dashboard.door_leerling"](),
      count: week.doorLeerling,
    },
    {
      weekStart: new Date(week.weekStart),
      serie: m["admin.dashboard.door_docent"](),
      count: week.doorDocent,
    },
    {
      weekStart: new Date(week.weekStart),
      serie: "Sociaal-emotioneel",
      count: week.sociaalEmotioneel,
    },
  ]);

  const maandRows = trends.nieuweScholenPerMaand.map((maand) => ({
    maand: `${new Date(Date.UTC(maand.jaar, maand.maand - 1, 1)).toLocaleDateString("nl-NL", {
      month: "short",
      timeZone: "UTC",
    })} '${String(maand.jaar).slice(-2)}`,
    count: maand.count,
  }));

  return (
    <Page>
      <Page.Header>
        <Page.Title>Dashboard</Page.Title>
      </Page.Header>
      <Page.Body>
        <div className="flex flex-col gap-8">
          <Page.Container className="grid grid-cols-1 items-stretch gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile
              title="Abonnementen"
              value={overview.licenties.abonnement}
              subtitle={`${overview.licenties.proefperiode} proefperiodes · ${overview.licenties.verlopen} verlopen`}
              icon={<IconMaterialSymbolsContractOutline className={iconClassName} aria-hidden />}
              history={{
                stat: "Abonnementen",
                description:
                  "Benadering: afsluitmoment en einddatum van de huidige licentie, wijzigingen worden niet historisch bewaard.",
              }}
            />
            <StatTile
              title={m["admin.dashboard.actieve_scholen"]()}
              value={`${actieveScholenPct}%`}
              subtitle={`${overview.actieveScholenMetRecenteLes} van ${overview.actieveScholen} met les in de laatste 30 dagen`}
              icon={<IconMaterialSymbolsSchoolOutline className={iconClassName} aria-hidden />}
              history={{ stat: "ActieveScholenMetRecenteLes", label: "Scholen met recente les" }}
            />
            <StatTile
              title="Nieuwe scholen"
              value={overview.nieuweScholenLaatste48Uur}
              subtitle="aangemeld in de laatste 48 uur"
              icon={<IconMaterialSymbolsNewReleases className={iconClassName} aria-hidden />}
              history={{ stat: "NieuweScholen", label: "Nieuwe scholen per week" }}
            />
            <StatTile
              title="Actieve gebruikers"
              value={overview.wekelijksActieveUsers}
              subtitle="afgelopen 7 dagen"
              icon={<IconMaterialSymbolsGroupOutline className={iconClassName} aria-hidden />}
              history={{
                stat: "ActieveDocenten",
                label: "Docenten met lesactiviteit",
                description:
                  "Benadering: docenten met lesactiviteit per week — logins worden niet historisch bewaard.",
              }}
            />
            <StatTile
              title="Leerlingen"
              value={overview.totaalLeerlingen.toLocaleString("nl-NL")}
              subtitle="totaal, alle scholen"
              icon={<IconMaterialSymbolsFaceOutline className={iconClassName} aria-hidden />}
              history={{ stat: "TotaalLeerlingen" }}
            />
            <StatTile
              title="Scholen met leerlingen"
              value={overview.scholenMetLeerlingen}
              subtitle={`van ${overview.geactiveerdeScholen} geactiveerde scholen`}
              icon={<IconMaterialSymbolsUploadFileOutline className={iconClassName} aria-hidden />}
              history={{ stat: "ScholenMetLeerlingen", label: "Scholen met leerlingen" }}
            />
            <StatTile
              title="Scholen zonder leerlingen"
              value={overview.scholenZonderLeerlingen}
              subtitle="geactiveerd maar nog niets geïmporteerd"
              icon={<IconMaterialSymbolsPersonOffOutline className={iconClassName} aria-hidden />}
              history={{ stat: "ScholenZonderLeerlingen" }}
            />
            <StatTile
              title="Wachtend op activatie"
              value={overview.wachtendOpActivatie}
              subtitle={m["admin.dashboard.sub_zonder_docent"]()}
              icon={<IconMaterialSymbolsHourglassEmpty className={iconClassName} aria-hidden />}
              history={{ stat: "WachtendOpActivatie" }}
            />
            <StatTile
              title={m["admin.dashboard.lessen_gegeven"]()}
              value={overview.totaalLessenGegeven.toLocaleString("nl-NL")}
              subtitle={m["admin.dashboard.sub_afgerond"]()}
              icon={<IconMaterialSymbolsSportsGymnastics className={iconClassName} aria-hidden />}
              history={{ stat: "LessenGegeven" }}
            />
            <StatTile
              title="Scores ingevoerd"
              value={overview.totaalScores.toLocaleString("nl-NL")}
              subtitle={m["admin.dashboard.sub_resultaten"]()}
              icon={<IconMaterialSymbolsMonitoring className={iconClassName} aria-hidden />}
              history={{ stat: "ScoresIngevoerd" }}
            />
            <StatTile
              title={m["admin.dashboard.tijd_tot_eerste_les"]()}
              value={
                overview.mediaanDagenTotEersteLes != null
                  ? `${Math.round(overview.mediaanDagenTotEersteLes)} dagen`
                  : "-"
              }
              subtitle="mediaan, scholen geactiveerd in laatste 90 dagen"
              icon={<IconMaterialSymbolsTimerOutline className={iconClassName} aria-hidden />}
              history={{
                stat: "MediaanDagenTotEersteLes",
                label: "Mediaan dagen tot eerste les",
                description:
                  "Mediaan in dagen, per week geëvalueerd over het rollende 90-dagen cohort.",
              }}
            />
            <StatTile
              title="Open tickets"
              value={overview.tickets.open}
              subtitle={
                `${overview.tickets.wachtOpReactie} wachten op reactie` +
                (overview.tickets.mediaanEersteReactieUren != null
                  ? ` · eerste reactie ~${Math.round(overview.tickets.mediaanEersteReactieUren)} uur`
                  : "")
              }
              icon={<IconMaterialSymbolsSupportAgent className={iconClassName} aria-hidden />}
              history={{ stat: "OpenTickets" }}
            />
            <StatTile
              title="Openstaande invites"
              value={overview.pendingInvites.totaal}
              subtitle={`${overview.pendingInvites.verlopen} verlopen · ${overview.pendingInvites.ouderDanDertigDagen} ouder dan 30 dagen`}
              icon={<IconMaterialSymbolsMailOutline className={iconClassName} aria-hidden />}
              history={{ stat: "OpenstaandeInvites" }}
            />
            <StatTile
              title={m["admin.dashboard.showcase_scholen"]()}
              value={overview.licenties.showcase}
              icon={<IconMaterialSymbolsBadgeOutline className={iconClassName} aria-hidden />}
              history={{
                stat: "ShowcaseScholen",
                description: "Benadering op basis van de aanmaakdatum van showcase-scholen.",
              }}
            />
          </Page.Container>

          <Page.Container className="grid grid-cols-1 gap-2 xl:grid-cols-2">
            <WeeklyTrendChart
              className="xl:col-span-2"
              title={m["admin.dashboard.aria_lessen_afgerond_per_week"]()}
              ariaLabel={m["admin.dashboard.aria_lessen_per_week"]()}
              rows={lessenRows}
              series={[{ label: m["admin.dashboard.lessen_afgerond"](), color: "var(--chart-1)" }]}
            />
            <WeeklyTrendChart
              title="Resultaten per week"
              ariaLabel={m["admin.dashboard.aria_resultaten_per_week"]()}
              rows={resultatenRows}
              series={RESULTATEN_SERIES}
            />
            <MonthlyBarChart
              title={m["admin.dashboard.nieuwe_scholen_per_maand"]()}
              ariaLabel={m["admin.dashboard.aria_nieuwe_scholen"]()}
              rows={maandRows}
            />
          </Page.Container>
        </div>
      </Page.Body>
    </Page>
  );
}
